import React from 'react';
import { Alert, Autocomplete, Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { ActivityCandidate, ActivityNode } from './activityplanning.models';

type Props = {
  selectedNode: ActivityNode | null;
  saving: boolean;
  loading: boolean;
  planningCycleName: string;
  budgetPeriodName: string;
  costCenterCode: string;
  activityCandidates: ActivityCandidate[];
  selectedActivity: ActivityCandidate | null;
  activityWeight: number;
  driverValue: number;
  targetValue: number;
  activityDescription: string;
  onActivitySelect: (value: ActivityCandidate | null) => void;
  onFieldChange: (name: string, value: any) => void;
  addActivity: () => void;
};

export default class ActivityFormPanel extends React.Component<Props> {
  render() {
    const p = this.props;
    const canAddActivity = p.selectedNode?.nodetype === 'KPI';
    const selectedActivity = p.selectedNode?.nodetype === 'ACTIVITY';

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Context Actions</Typography>
        {p.selectedNode ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>Selected:</strong> {p.selectedNode.nodetype} — {p.selectedNode.code}</Typography>
            <Typography variant="body2" color="text.secondary">{p.selectedNode.name}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an Indicator to add an Activity.
          </Typography>
        )}

        {canAddActivity && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Activity</Typography>
            <Stack spacing={1.5}>
              <TextField size="small" label="Planning Cycle" value={p.planningCycleName} disabled />
              <TextField size="small" label="Budget Period" value={p.budgetPeriodName} disabled />
              <TextField size="small" label="Cost Center" value={p.costCenterCode} disabled />
              <Autocomplete
                size="small"
                options={p.activityCandidates}
                value={p.selectedActivity}
                onChange={(_, value) => p.onActivitySelect(value)}
                getOptionLabel={(x) => `${x.shc} — ${x.nam}`}
                isOptionEqualToValue={(a, b) => a.rid === b.rid}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Activity"
                    helperText="Select a reusable Activity. Its reusable Tasks are added automatically."
                  />
                )}
              />
              <TextField size="small" label="Indicator Contribution Weight" type="number" value={p.activityWeight}
                onChange={(e) => p.onFieldChange('activityWeight', Number(e.target.value || 0))} />
              <TextField size="small" label="Driver Value" type="number" value={p.driverValue}
                onChange={(e) => p.onFieldChange('driverValue', Number(e.target.value || 0))} />
              <TextField size="small" label="Target Value" type="number" value={p.targetValue}
                onChange={(e) => p.onFieldChange('targetValue', Number(e.target.value || 0))} />
              <TextField size="small" label="Expected Result / Planning Notes" value={p.activityDescription}
                onChange={(e) => p.onFieldChange('activityDescription', e.target.value)} multiline minRows={2} />
              <Button variant="contained" onClick={p.addActivity} disabled={!p.selectedActivity || p.saving}>Add Activity</Button>
            </Stack>
          </>
        )}

        {selectedActivity && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info">
              Tasks are reusable institutional definitions and are instantiated automatically. Select a Task to prepare its budget lines.
            </Alert>
          </>
        )}
      </Paper>
    );
  }
}
